import React, { useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { useAdminUserKycDetailsQuery } from "../../lib/api/useUserActivityQueries";
import Profile from "../kycLogs/Profile";
import Kyc2 from "../kycLogs/Kyc2";
import Kyc3 from "../kycLogs/Kyc3";
import {
  getFirstVisibleLevel,
  showLevel1Tab,
  showLevel2Tab,
  showLevel3Tab,
} from "./kycUtils";

const Kyc = ({ customerId }: { customerId: string }) => {
  const {
    data: customerDeet,
    isPending: loading,
    isError,
    error,
  } = useAdminUserKycDetailsQuery(customerId);
  const [level, setLevel] = useState<number>(1);

  useEffect(() => {
    if (!customerDeet) return;
    const visible =
      (level === 1 && showLevel1Tab(customerDeet)) ||
      (level === 2 && showLevel2Tab(customerDeet)) ||
      (level === 3 && showLevel3Tab(customerDeet));
    if (!visible) setLevel(getFirstVisibleLevel(customerDeet));
  }, [customerDeet, level]);

  if (loading) {
    return (
      <div className="w-full p-4 sm:p-6 bg-[#192025] font-manrope flex justify-center items-center min-h-[280px]">
        <TailSpin color="#0C84DA" height={44} width={44} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full p-4 sm:p-6 bg-[#192025] font-manrope">
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-red-400 text-[14px]">
          {(error?.response?.data as { message?: string } | undefined)
            ?.message ?? "Failed to load KYC details"}
        </div>
      </div>
    );
  }

  const level1Visible = showLevel1Tab(customerDeet);
  const level2Visible = showLevel2Tab(customerDeet);
  const level3Visible = showLevel3Tab(customerDeet);
  const noLevelsStarted = !level1Visible && !level2Visible && !level3Visible;

  if (noLevelsStarted) {
    return (
      <div className="w-full p-4 sm:p-6 bg-[#192025] font-manrope">
        <div className="rounded-xl border border-[#1C2227] bg-[#0D0D0D] p-6 text-center">
          <p className="text-[#869AA9] text-[14px]">
            No KYC levels started yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 bg-[#192025] font-manrope">
      <div className="h-full flex justify-start items-center min-w-max">
        {level1Visible && (
          <button
            onClick={() => setLevel(1)}
            className={
              level === 1
                ? "h-full pb-2 border-b-2 font-bold text-[14px] sm:text-[16px] text-secondary_button border-secondary_button px-3 sm:px-4"
                : "h-full pb-2 text-[12px] sm:text-[14px] text-gray-200 px-3 sm:px-4"
            }
          >
            Level 1
          </button>
        )}
        {level2Visible && (
          <button
            onClick={() => setLevel(2)}
            className={
              level === 2
                ? "h-full pb-2 border-b-2 font-bold text-[14px] sm:text-[16px] text-secondary_button border-secondary_button px-3 sm:px-4"
                : "h-full pb-2 text-[12px] sm:text-[14px] text-gray-200 px-3 sm:px-4"
            }
          >
            Level 2
          </button>
        )}
        {level3Visible && (
          <button
            onClick={() => setLevel(3)}
            className={
              level === 3
                ? "h-full pb-2 border-b-2 font-bold text-[14px] sm:text-[16px] text-secondary_button border-secondary_button px-3 sm:px-4"
                : "h-full pb-2 text-[12px] sm:text-[14px] text-gray-200 px-3 sm:px-4"
            }
          >
            Level 3
          </button>
        )}
      </div>
      {level === 1 ? (
        <Profile customerDeet={customerDeet} />
      ) : level === 2 ? (
        <Kyc2 customerDeet={customerDeet} />
      ) : level === 3 ? (
        <Kyc3 customerDeet={customerDeet} />
      ) : null}
    </div>
  );
};

export default Kyc;
